import { useState, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { Content, Actor } from "../utils/types";
import { contentService } from "../services/contentService";
import { useAuthContext } from "../components/AuthContext";

interface UseMyContentsReturn {
  contents: Content[];
  purchasedContents: Content[];
  loading: boolean;
  finishedFetch: boolean;
  refreshContents: () => Promise<void>;
}

export function useMyContents(): UseMyContentsReturn {
  const [contents, setContents] = useState<Content[]>([]);
  const [purchasedContents, setPurchasedContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [finishedFetch, setFinishedFetch] = useState<boolean>(false);
  const { user } = useAuthContext();
  const auth = getAuth();

  const transformContentData = (
    contents: Content[],
    purchasedData: { id: string; chapters: number[] }[],
  ) => {
    return contents.map((c) => {
      const chapters = c.chapters.filter((ch) => {
        const purchasedChapters =
          purchasedData.filter((pc) => pc.id === c.id)[0]?.chapters || [];
        return purchasedChapters.includes(ch.index);
      });

      return {
        id: c.id,
        title: c.title,
        category: c.category,
        actors: c.actors.map((a: Actor) => ({
          name: a.name,
          role: a.role,
          image: `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/contents%2Factors%2F${a.image}.png?alt=media`,
        })),
        chapters: chapters,
        bgColor: c.bgColor,
      } as Content;
    });
  };

  const fetchContentsData = async (uid: string) => {
    try {
      const purchasedData = await contentService.fetchPurchasedContents(uid);
      const contents = await contentService.fetchContents(purchasedData);

      if (contents.length === 0) return;

      setContents(contents);
      setPurchasedContents(transformContentData(contents, purchasedData));
    } catch (error) {
      console.error("Failed to fetch contents:", error);
      alert("エラーが発生しました。\nしばらくしてから再度お試しください。");
    } finally {
      setLoading(false);
      setFinishedFetch(true);
    }
  };

  const refreshContents = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    await fetchContentsData(user.uid);
  }, [user?.uid]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (uid) {
      fetchContentsData(uid);
    } else {
      setLoading(false);
    }
  }, [auth.currentUser?.uid]);

  return {
    contents,
    purchasedContents,
    loading,
    finishedFetch,
    refreshContents,
  };
}
